"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { Maximize2, Minimize2 } from "lucide-react";

/** Keep the same planner mounted when entering the browser's modal top layer. */
export function PlannerWorkspace({ children, active = true }: { children: ReactNode; active?: boolean }) {
  const [view, setView] = useState({ expanded: false, inlineHeight: 0 });
  const dialogRef = useRef<HTMLDialogElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasExpanded = useRef(false);
  if (!active && view.expanded) setView(value => ({ ...value, expanded: false }));

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const returning = wasExpanded.current;
    wasExpanded.current = view.expanded;
    if (!view.expanded && !returning) return;
    dialog.close();
    if (!view.expanded) {
      dialog.show();
      toggleRef.current?.focus({ preventScroll: true });
      return;
    }
    dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    toggleRef.current?.focus({ preventScroll: true });
    dialog.scrollTop = 0;
    return () => { document.body.style.overflow = previousOverflow; };
  }, [view.expanded]);

  return <div className="min-w-0" style={view.expanded ? { height: view.inlineHeight } : undefined}>
    <dialog ref={dialogRef} open role={view.expanded ? "dialog" : "region"}
      aria-modal={view.expanded || undefined} aria-label={view.expanded ? "Full screen route planner" : "Route planning workspace"}
      onCancel={event => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        setView(value => ({ ...value, expanded: false }));
      }}
      onKeyDown={event => {
        // The map also has an inline dialog. Once its modal closes, handle
        // Escape here before that inline dialog consumes the browser action.
        if (event.key !== "Escape" || event.defaultPrevented || !view.expanded || event.currentTarget.querySelector("dialog:modal")) return;
        event.preventDefault();
        setView(value => ({ ...value, expanded: false }));
      }}
      className={view.expanded
        ? "fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none overflow-y-auto overscroll-contain border-0 bg-warm-bg p-0 text-navy backdrop:bg-navy/50"
        : "relative m-0 w-full max-w-none overflow-visible border-0 bg-transparent p-0 text-navy"}>
      <div className={view.expanded ? "sticky top-0 z-40 border-b border-border bg-warm-bg px-4 pt-[env(safe-area-inset-top)] md:px-8" : "mb-4"}>
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <Link href="/plan/route" className="inline-flex min-h-11 items-center text-sm font-semibold text-teal underline underline-offset-4">Choose another resort</Link>
          <button ref={toggleRef} type="button" aria-pressed={view.expanded}
            onClick={() => setView(value => ({ expanded: !value.expanded, inlineHeight: value.expanded ? value.inlineHeight : dialogRef.current?.offsetHeight ?? 0 }))}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-navy hover:border-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal">
            {view.expanded ? <Minimize2 size={18} aria-hidden /> : <Maximize2 size={18} aria-hidden />}
            {view.expanded ? "Exit full screen" : "Full screen"}
          </button>
        </div>
      </div>
      <div className={view.expanded ? "mx-auto max-w-[1600px] px-4 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:px-8 md:py-8" : undefined}>{children}</div>
    </dialog>
  </div>;
}
